import './styles.scss';

import { Boundary, Kind, Type, KINDS, Magnitude, ValueTunerConditionRaw } from '../../dataModel';
import { Button, Input } from '@myua/kit';
import { SearchNormal } from '@myua/icons/dist/linear16';
import React from 'react';
import get from 'lodash/get';

const INPUTS: Partial<Record<Boundary, { placeholder: string; icon?: JSX.Element }>> = {
  [Boundary.GE]: {
    placeholder: 'From',
  },
  [Boundary.LE]: {
    placeholder: 'To',
  },
  [Boundary.TEXT]: {
    placeholder: 'Search',
    icon: <SearchNormal />,
  },
};

export const makeInput = function <B extends Boundary>(
  boundary: B,
  ctx: {
    inputOnKeyPress: () => void;
    itemOnChange: (b: B) => (e: React.ChangeEvent<HTMLInputElement>) => void;
    condition: ValueTunerConditionRaw;
  },
) {
  return (
    <Input
      key={boundary}
      prefix={INPUTS[boundary]?.icon}
      className={`gen-filter-tuner__input-${boundary}`}
      placeholder={INPUTS[boundary]?.placeholder}
      value={get(ctx.condition, boundary, '')}
      onChange={ctx.itemOnChange(boundary)}
      onKeyPress={ctx.inputOnKeyPress}
    />
  );
};

type ValueTunerProps = {
  condition: ValueTunerConditionRaw;
  inputOnKeyPress: any;
  disabled: boolean;
  applyOnClick: () => void;
  closeOnClick: () => void;
  clearOnClick: () => void;
  type: Type;
  multiplier: Magnitude;
  setMultiplier: (m: Magnitude) => void;
  itemOnChange: (b: Boundary) => (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const magnitudes: Magnitude[] = ['B', 'KB', 'MB', 'GB'];
const Multiplier: React.FC<{
  magnitude: Magnitude;
  setMagnitude: (m: Magnitude) => void;
}> = ({ setMagnitude, magnitude }) => {
  return (
    <div className="magnitude">
      {magnitudes.map((mgn) => (
        <button
          key={mgn}
          className={`magnitude__selector ${magnitude === mgn ? 'selected' : ''}`}
          onClick={() => setMagnitude(mgn)}
        >
          {mgn.toUpperCase()}
        </button>
      ))}
    </div>
  );
};

export function ValueTuner({
  condition,
  itemOnChange,
  inputOnKeyPress,
  disabled,
  applyOnClick,
  closeOnClick,
  clearOnClick,
  multiplier,
  setMultiplier,
  type,
}: ValueTunerProps) {
  const kind = KINDS[type];

  const literal = [
    makeInput<Boundary.TEXT>(Boundary.TEXT, {
      inputOnKeyPress,
      itemOnChange,
      condition,
    }),
  ];
  const numerical = [
    makeInput(Boundary.GE, {
      inputOnKeyPress,
      itemOnChange,
      condition,
    }),
    makeInput(Boundary.LE, {
      inputOnKeyPress,
      itemOnChange,
      condition,
    }),
  ];
  return (
    <div className="gen-filter-tuner gen-filter-tuner_value">
      <div className="gen-filter-tuner__inputs-row">
        {kind === Kind.LITERAL
          ? literal
          : type === Type.BYTES
            ? [
                ...numerical,
                <Multiplier key="multiplier" magnitude={multiplier} setMagnitude={setMultiplier} />,
              ]
            : numerical}
      </div>
      {disabled ? (
        <div className="gen-filter-tuner__info">{'Maximum value is less than minimal'}</div>
      ) : (
        ''
      )}
      <div className="gen-filter-tuner__buttons-row">
        <Button htmlType="button" onClick={clearOnClick}>
          Clear
        </Button>
        <Button
          type="primary"
          // Когда границы противоречат друг другу, кнопка не просто гаснет, а
          // подсвечивается как ошибка — так было и на старой кнопке-обводке.
          danger={disabled}
          htmlType="button"
          disabled={disabled}
          onClick={applyOnClick}
        >
          Apply
        </Button>
        <Button htmlType="button" onClick={closeOnClick}>
          Close
        </Button>
      </div>
    </div>
  );
}
